import React, {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  AvatarGroup,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import {
  useAuth,
} from '../../contexts/AuthContext';
import {
  eventsService,
} from '../../services/events';
import EventHero from './EventHero';
import { resolveMediaUrl } from '../../utils/mediaUrl';

function EventList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await eventsService.getEvents();
      if (result.success) {
        // Sort events by start_date (most recent first)
        const sortedEvents = result.data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        setEvents(sortedEvents);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const getNextEvent = () => {
    const now = new Date();
    // Find events that are in the future or happening now (within 24 hours)
    const upcomingEvents = events.filter(event => {
      const eventStartDate = new Date(event.start_date);
      const hoursDifference = (eventStartDate - now) / (1000 * 60 * 60);
      // Event is upcoming if it's in the future, or ongoing if within 24 hours of the event time
      return hoursDifference > -24;
    });
    
    if (upcomingEvents.length > 0) {
      // Sort by start_date ascending (earliest first) and return the first one
      const sortedUpcoming = upcomingEvents.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      return sortedUpcoming[0];
    }
    
    return null;
  };

  const getPastEvents = () => {
    const now = new Date();
    // Find events that are more than 24 hours in the past
    const pastEvents = events.filter(event => {
      const eventStartDate = new Date(event.start_date);
      const hoursDifference = (eventStartDate - now) / (1000 * 60 * 60);
      return hoursDifference <= -24;
    });
    // Sort past events by start_date descending (most recent first)
    return pastEvents.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return '#C9A227';
      case 'ongoing': return '#E85D4C';
      case 'completed': return '#6B8F71';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#14110E',
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: '#C9A227'
          }}
        />
      </Box>
    );
  }

  const nextEvent = getNextEvent();
  const pastEvents = getPastEvents();

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', bgcolor: '#14110E' }}>
      {/* Error Alert */}
      {error && (
        <Container maxWidth="lg" sx={{ pt: 3 }}>
          <Alert
            severity="error"
            sx={{
              bgcolor: alpha('#EF4444', 0.1),
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#FCA5A5',
            }}
          >
            {error}
          </Alert>
        </Container>
      )}

      {/* Hero Section - Next Event */}
      {nextEvent ? (
        <EventHero event={nextEvent} />
      ) : (
        // No upcoming event - minimal hero
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#14110E',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white', px: { xs: 2, sm: 3 } }}>
            <EventIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#C9A227', mb: 3 }} />
            <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: '1.75rem', sm: '2.5rem' } }}>
              No Upcoming Events
            </Typography>
            <Typography variant="body1" sx={{ color: alpha('#fff', 0.7), fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Check back soon for the next OC-LANgenthal!
            </Typography>
          </Box>
        </Box>
      )}

      {/* Timeline Archive Section */}
      {pastEvents.length > 0 && (
        <Box
          sx={{
            backgroundColor: '#14110E',
            position: 'relative',
            py: { xs: 6, sm: 8, md: 12 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
            {/* Archive Header */}
            <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  mb: 2,
                  color: 'white',
                }}
              >
                Archive
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: alpha('#fff', 0.7),
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                Past OC-LANgenthal Events
              </Typography>
            </Box>

            {/* Timeline */}
            <Box sx={{ position: 'relative' }}>
              {/* Timeline Line - Desktop only */}
              {!isTablet && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: '#C9A227',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}

              {/* Timeline Events */}
              {pastEvents.map((event, index) => {
                const eventId = event.id || event._id;
                const isLeft = index % 2 === 0;
                const isRegistered = user && event.participants.includes(user.id || user._id);

                return (
                  <Box
                    key={eventId}
                    sx={{
                      position: 'relative',
                      mb: { xs: 6, sm: 8 },
                      display: 'flex',
                      flexDirection: { xs: 'column', md: isLeft ? 'row' : 'row-reverse' },
                      alignItems: 'center',
                      gap: { xs: 0, md: 4 },
                    }}
                  >
                    {/* Date Badge - Desktop */}
                    {!isTablet && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          bgcolor: '#241E18',
                          border: '3px solid #C9A227',
                          borderRadius: '50%',
                          width: 100,
                          height: 100,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#C9A227',
                            lineHeight: 1,
                            mb: 0.5,
                          }}
                        >
                          {new Date(event.start_date).getDate()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: alpha('#fff', 0.7),
                            textTransform: 'uppercase',
                            lineHeight: 1,
                          }}
                        >
                          {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short' })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.7rem',
                            color: alpha('#fff', 0.5),
                            lineHeight: 1,
                            mt: 0.5,
                          }}
                        >
                          {new Date(event.start_date).getFullYear()}
                        </Typography>
                      </Box>
                    )}

                    {/* Event Card */}
                    <Box
                      sx={{
                        width: { xs: '100%', md: 'calc(50% - 60px)' },
                        bgcolor: alpha('#241E18', 0.8),
                        border: '1px solid rgba(201, 162, 39, 0.2)',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: alpha('#C9A227', 0.5),
                        },
                      }}
                      onClick={() => navigate(`/events/${eventId}`)}
                    >
                      {/* Event Image */}
                      {event.image_url ? (
                        <Box
                          sx={{
                            height: 200,
                            backgroundImage: `url(${resolveMediaUrl(event.image_url)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '50%',
                              background: 'linear-gradient(to top, rgba(36, 30, 24, 0.9) 0%, transparent 100%)',
                            },
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 200,
                            backgroundColor: '#241E18',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          <EventIcon sx={{ fontSize: 80, color: alpha('#fff', 0.3) }} />
                        </Box>
                      )}

                      {/* Event Content */}
                      <Box sx={{ p: 3 }}>
                        {/* Date Badge - Mobile */}
                        {isTablet && (
                          <Chip
                            icon={<CalendarIcon />}
                            label={formatDate(event.start_date)}
                            sx={{
                              mb: 2,
                              bgcolor: alpha('#C9A227', 0.2),
                              color: '#C9A227',
                              border: '1px solid rgba(201, 162, 39, 0.3)',
                              fontWeight: 600,
                            }}
                          />
                        )}

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: 'white',
                            mb: 2,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          }}
                        >
                          {event.name}
                        </Typography>

                        {event.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: alpha('#fff', 0.7),
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {event.description}
                          </Typography>
                        )}

                        {/* Event Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 16, color: '#3D7A8C' }} />
                            <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {event.location}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon sx={{ fontSize: 16, color: '#3D7A8C' }} />
                            <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {event.participant_count} participants
                            </Typography>
                          </Box>
                        </Box>

                        {/* Participant Avatars */}
                        {event.participant_details && event.participant_details.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <AvatarGroup
                              max={8}
                              sx={{
                                '& .MuiAvatar-root': {
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.875rem',
                                  border: '2px solid #241E18',
                                  bgcolor: alpha('#C9A227', 0.3),
                                },
                              }}
                            >
                              {event.participant_details.slice(0, 8).map((participant, idx) => (
                                <Avatar
                                  key={idx}
                                  src={resolveMediaUrl(participant.avatar)}
                                  alt={participant.username}
                                >
                                  {participant.username?.charAt(0).toUpperCase()}
                                </Avatar>
                              ))}
                            </AvatarGroup>
                          </Box>
                        )}

                        {/* Status & Registration Badge */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={event.status}
                            size="small"
                            sx={{
                              bgcolor: alpha(getStatusColor(event.status), 0.2),
                              color: getStatusColor(event.status),
                              border: `1px solid ${alpha(getStatusColor(event.status), 0.3)}`,
                              fontWeight: 600,
                            }}
                          />
                          {isRegistered && (
                            <Chip
                              label="Participated"
                              size="small"
                              sx={{
                                bgcolor: alpha('#6B8F71', 0.2),
                                color: '#6B8F71',
                                border: '1px solid rgba(107, 143, 113, 0.3)',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Container>
        </Box>
      )}
    </Box>
  );
}

export default EventList;
